import React, { useRef } from 'react';
import BaseLayout from '../../components/layout/BaseLayout';
import CarouselSection from '../../components/sections/Carousel/CarouselSection';
import CaseStudySection from '../../components/sections/CaseStudy/CaseStudySection';
import HowWeWorkSection from '../../components/sections/HowWeWork/HowWeWorkSection';
import NextStepsSection from '../../components/sections/NextSteps/NextStepsSection';
import TechnologiesSection from '../../components/sections/Technologies/TechnologiesSection';
import LogosScroll from '../../components/sections/LogosScroll/LogosScroll';
import IndustryCards from '../../components/sections/Industry/IndustryCards';
import EnterpriseCardsSection from '../../components/sections/EnterpriseCards/EnterpriseCardsSection';
import SoftwareAgencyCardsSection from '../../components/sections/SoftwareAgencyCards/SoftwareAgencyCardsSection';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useNavigate } from "react-router-dom";
import HeroSectionWithLogos from '../../components/sections/Hero/HeroSectionWithLogos';
import RecognitionSection from '../../components/sections/Recognition/RecognitionSection';
import { useTranslation } from '../../hooks/useTranslation';


interface ServicePageProps {
    heroTitle: React.ReactNode;
    heroSubtitle: React.ReactNode;
    industryTitle: React.ReactNode;
    industryCards: Array<{ text: React.ReactNode; }>;
    howWeWorkTitle: React.ReactNode;
    howWeWorkSteps: Array<{
        title: React.ReactNode;
        description: React.ReactNode[];
    }>;
    nextSteps: Array<{ title: React.ReactNode; }>;
    technologiesTitle: React.ReactNode;
    technologies: Array<{ name: string; logo: string; }>;
    recognition?: {
        title: React.ReactNode;
        subtitle: Array<React.ReactNode>;
        recognitionImage: string;
    };
    caseStudyTitle: React.ReactNode;
    enterpriseCards?: Array<{
        title: React.ReactNode;
        description: React.ReactNode;
    }>;
    softwareAgencyCards?: Array<{
        title: React.ReactNode;
        description: React.ReactNode;
    }>;
}

const ServiceTemplate: React.FC<ServicePageProps> = ({
    heroTitle,
    heroSubtitle,
    industryTitle,
    industryCards,
    howWeWorkTitle,
    howWeWorkSteps,
    nextSteps,
    technologiesTitle,
    technologies,
    recognition,
    caseStudyTitle,
    enterpriseCards,
    softwareAgencyCards
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const industryRef = useRef<HTMLDivElement>(null);
    const howWeWorkRef = useRef<HTMLDivElement>(null);

    const heroButtons = [
        {
            label: t("service.heroButtons.forWho"),
            variant: "secondary",
            onClick: () => {
                if (industryRef.current) {
                    const elementPosition = industryRef.current.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 64;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            },
            rightIcon: <MdKeyboardArrowRight size={32} />,
        },
        {
            label: t("service.heroButtons.specializations"),
            variant: "outlineNoTheme",
            onClick: () => {
                if (howWeWorkRef.current) {
                    const elementPosition = howWeWorkRef.current.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 64;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            },
            rightIcon: <MdKeyboardArrowRight size={32} />,
        },
    ];

    return (
        <BaseLayout>
            <HeroSectionWithLogos
                title={heroTitle}
                subtitle={heroSubtitle}
                heroButtons={heroButtons}
                heroLottieAnimation={require('../../assets/animations/Sygnet-Appotype.json')}
                small={true}
            />
            <div ref={industryRef}>
                <IndustryCards
                    title={industryTitle}
                    cards={industryCards}
                />
            </div>
            <div ref={howWeWorkRef}>
                <HowWeWorkSection
                    title={howWeWorkTitle}
                    steps={howWeWorkSteps}
                />
            </div>
            {softwareAgencyCards && (
                <SoftwareAgencyCardsSection
                    title={t("service.cta.expertTask")}
                    subtitle={t("service.cta.expertSubtitle")}
                    cards={softwareAgencyCards}
                />
            )}
            <NextStepsSection
                title={t("service.nextSteps.title")}
                steps={nextSteps}
            />
            <TechnologiesSection
                title={technologiesTitle}
                technologies={technologies}
            />
            {enterpriseCards && (
                <EnterpriseCardsSection
                    title={t("service.enterprise.title")}
                    subtitle={t("service.enterprise.subtitle")}
                    cards={enterpriseCards}
                />
            )}
            {recognition && (
                <RecognitionSection
                    title={recognition.title}
                    subtitle={recognition.subtitle}
                    recognitionImage={recognition.recognitionImage}
                />
            )}
            <CarouselSection showLogos={false} />
            <CaseStudySection title={caseStudyTitle} />
        </BaseLayout>
    );
};

export default ServiceTemplate; 